import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreateActivityInput } from '@/types/businessAppTypes';
import { Loader2, Plus, MapPin, DollarSign, Users, Settings, CheckCircle, Image } from 'lucide-react';
import { ImagePicker } from '../../../shared/ImagePicker';

interface CreateActivityModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateActivityInput) => void;
  businessLocation: [number, number];
  isLoading?: boolean;
  businessImages?: string[];
}

export function CreateActivityModal({
  open,
  onClose,
  onSubmit,
  businessLocation,
  isLoading = false,
  businessImages = []
}: CreateActivityModalProps) {
  const [formData, setFormData] = useState<CreateActivityInput>({
    name: '',
    description: '',
    activityType: 'observation',
    type: 'business',
    location: {
      type: 'Point',
      coordinates: businessLocation
    },
    pricing: {
      defaultPrices: {
        adult: { amount: 0, currency: 'USD' },
        child: { amount: 0, currency: 'USD' },
        student: { amount: 0, currency: 'USD' }
      }
    },
    maxParticipants: 10,
    isActive: true,
    images: []
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const getActivityTypeIcon = (type: string) => {
    const iconMap: Record<string, any> = {
      observation: '🔭',
      photography: '📸',
      research: '🔬',
      feeding: '🍃',
      tour: '🚶‍♂️',
      educational: '📚'
    };
    return iconMap[type] || '🔭';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto border-slate-200/60 shadow-xl shadow-slate-900/10">
        <DialogHeader className="pb-6 border-b border-slate-100/80">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-900">Create New Activity</DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                Set up a new wildlife activity for your customers to book and enjoy
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center shadow-sm">
                  <Settings className="w-4 h-4 text-blue-600" />
                </div>
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="space-y-2">
                <Label htmlFor="name">Activity Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Safari Photography Tour, Wildlife Observation"
                  required
                  disabled={isLoading}
                  className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm"
                />
                <p className="text-xs text-slate-600">
                  Choose a descriptive name that highlights the activity's appeal
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what makes this activity special, what participants will experience, and any highlights..."
                  rows={4}
                  required
                  disabled={isLoading}
                  className="focus:ring-primary focus:border-primary resize-none border-slate-200/80 shadow-sm"
                />
                <p className="text-xs text-slate-600">
                  Provide details that help customers understand what to expect
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="activityType">Activity Type</Label>
                  <Select
                    value={formData.activityType}
                    onValueChange={(value) => setFormData({ ...formData, activityType: value })}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="observation">
                        <div className="flex items-center gap-2">
                          <span>🔭</span>
                          Observation
                        </div>
                      </SelectItem>
                      <SelectItem value="photography">
                        <div className="flex items-center gap-2">
                          <span>📸</span>
                          Photography
                        </div>
                      </SelectItem>
                      <SelectItem value="research">
                        <div className="flex items-center gap-2">
                          <span>🔬</span>
                          Research
                        </div>
                      </SelectItem>
                      <SelectItem value="feeding">
                        <div className="flex items-center gap-2">
                          <span>🍃</span>
                          Feeding
                        </div>
                      </SelectItem>
                      <SelectItem value="tour">
                        <div className="flex items-center gap-2">
                          <span>🚶‍♂️</span>
                          Tour
                        </div>
                      </SelectItem>
                      <SelectItem value="educational">
                        <div className="flex items-center gap-2">
                          <span>📚</span>
                          Educational
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxParticipants" className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    Max Participants
                  </Label>
                  <Input
                    id="maxParticipants"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.maxParticipants}
                    onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) || 1 })}
                    required
                    disabled={isLoading}
                    className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm"
                  />
                  <p className="text-xs text-slate-600">
                    Maximum number of people per session
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Section */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200/50 flex items-center justify-center shadow-sm">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
                Pricing Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <p className="text-sm text-slate-600">
                Set competitive prices for different participant categories
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adultPrice" className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800 border border-blue-200/50">
                      Adult
                    </Badge>
                    Price (USD)
                  </Label>
                  <Input
                    id="adultPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.defaultPrices.adult.amount}
                    onChange={(e) => setFormData({
                      ...formData,
                      pricing: {
                        ...formData.pricing,
                        defaultPrices: {
                          ...formData.pricing.defaultPrices,
                          adult: {
                            ...formData.pricing.defaultPrices.adult,
                            amount: parseFloat(e.target.value) || 0
                          }
                        }
                      }
                    })}
                    required
                    disabled={isLoading}
                    className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="childPrice" className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800 border border-purple-200/50">
                      Child
                    </Badge>
                    Price (USD)
                  </Label>
                  <Input
                    id="childPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.defaultPrices.child.amount}
                    onChange={(e) => setFormData({
                      ...formData,
                      pricing: {
                        ...formData.pricing,
                        defaultPrices: {
                          ...formData.pricing.defaultPrices,
                          child: {
                            ...formData.pricing.defaultPrices.child,
                            amount: parseFloat(e.target.value) || 0
                          }
                        }
                      }
                    })}
                    required
                    disabled={isLoading}
                    className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="studentPrice" className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border border-emerald-200/50">
                      Student
                    </Badge>
                    Price (USD)
                  </Label>
                  <Input
                    id="studentPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricing.defaultPrices.student.amount}
                    onChange={(e) => setFormData({
                      ...formData,
                      pricing: {
                        ...formData.pricing,
                        defaultPrices: {
                          ...formData.pricing.defaultPrices,
                          student: {
                            ...formData.pricing.defaultPrices.student,
                            amount: parseFloat(e.target.value) || 0
                          }
                        }
                      }
                    })}
                    required
                    disabled={isLoading}
                    className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Image Section */}
          <ImagePicker
            label="Activity Image"
            description="Choose an image from your library to represent this activity"
            selectedImage={formData.images?.[0]}
            onImageChange={(imageUrl) => {
              setFormData({
                ...formData,
                images: imageUrl ? [imageUrl] : []
              });
            }}
            imageLibrary={businessImages}
            placeholder="Select from your image library"
            compact={true}
            disabled={isLoading}
          />

          {/* Availability & Status Section */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200/50 flex items-center justify-center shadow-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <Label htmlFor="isActive" className="text-base font-medium">
                      Activity Status
                    </Label>
                    <p className="text-sm text-slate-600">
                      Make this activity available for booking immediately
                    </p>
                  </div>
                </div>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  disabled={isLoading}
                />
              </div>
              {formData.isActive && (
                <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200/50">
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="w-4 h-4" />
                    Activity will be immediately available for customer bookings
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-200/60">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose} 
              disabled={isLoading}
              className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading} 
              className="bg-emerald-600 hover:bg-emerald-700 px-8 shadow-md hover:shadow-lg transition-shadow min-w-[140px]"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Activity
                </div>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 